import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Network } from "vis-network";
import { DataSet } from "vis-data";
import {
  FaWhatsapp, FaPlus, FaLightbulb, FaBolt, FaSearch,
  FaProjectDiagram, FaExclamationTriangle, FaUnlink, FaHandPointer,
  FaBox, FaRoute, FaReply, FaBars, FaTrash, FaPen,
  FaExpand, FaSearchPlus, FaSearchMinus, FaArrowsAlt
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import "./FlowBuilder.css";

const BASE_URL = "https://preprod.dashboard.rashailagro.in/whatsappwebhook";

const FlowBuilder = () => {
    const networkRef = useRef(null);
    const containerRef = useRef(null);
    
    // Datasets
    const [nodesDataset] = useState(() => new DataSet());
    const [edgesDataset] = useState(() => new DataSet());

    const [allNodesList, setAllNodesList] = useState([]);
    const [allProductsList, setAllProductsList] = useState([]);
    const [allTriggersList, setAllTriggersList] = useState([]);
    
    const [currentTriggerId, setCurrentTriggerId] = useState(null);
    const [currentTrigger, setCurrentTrigger] = useState(null);
    const [triggerSearch, setTriggerSearch] = useState("");
    
    const [isHierarchical, setIsHierarchical] = useState(true);
    const [userSelectedLayoutMode, setUserSelectedLayoutMode] = useState(null);
    
    const [showHintBar, setShowHintBar] = useState(true);
    const [showUnlinkedPanel, setShowUnlinkedPanel] = useState(false);
    const [unlinkedNodes, setUnlinkedNodes] = useState([]);
    
    // Editor State
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [nodeForm, setNodeForm] = useState(null);
    const [entryPoints, setEntryPoints] = useState([]);
    
    // Trigger Modal
    const [triggerModal, setTriggerModal] = useState({ open: false, data: {} });

    useEffect(() => {
        if (!containerRef.current) return;
        const options = {
            layout: { 
                hierarchical: { 
                    enabled: isHierarchical, 
                    direction: "UD", 
                    sortMethod: "directed", 
                    levelSeparation: 220, 
                    nodeSpacing: 250, 
                    treeSpacing: 250
                } 
            },
            physics: { enabled: false },
            edges: { smooth: { type: 'cubicBezier', forceDirection: 'vertical', roundness: 0.5 } },
            interaction: { hover: true, tooltipDelay: 100 }
        };
        const network = new Network(containerRef.current, { nodes: nodesDataset, edges: edgesDataset }, options);
        networkRef.current = network;

        network.on("dragEnd", (params) => {
            if (params.nodes && params.nodes.length > 0) {
                savePositionsToBackend(params.nodes, network);
                if (isHierarchical) {
                    setIsHierarchical(false);
                    setUserSelectedLayoutMode(false);
                    network.setOptions({ layout: { hierarchical: { enabled: false } } });
                    toast.info("Switched to Manual Freeform to retain custom positions.");
                }
            }
        });

        network.on("selectNode", (params) => {
            const nodeId = params.nodes[0];
            if (String(nodeId).startsWith('trigger_')) {
                const triggerId = String(nodeId).replace('trigger_', '');
                hideEditor();
                editTriggerById(triggerId);
                return;
            }
            loadNodeToEditor(nodeId);
        });
        
        network.on("deselectNode", () => {
            hideEditor();
        });

        return () => {
            if (networkRef.current) {
                networkRef.current.destroy();
                networkRef.current = null;
            }
        };
    }, []); // eslint-disable-line

    const fetchLookupData = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/flow/lookup-data/`);
            setAllNodesList(res.data.all_nodes || []);
            setAllProductsList(res.data.products || []);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchTriggers = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/flow/triggers/`);
            setAllTriggersList(res.data.triggers || []);
        } catch (error) {
            toast.error("Failed to load flows.");
        }
    };

    useEffect(() => {
        fetchLookupData();
        fetchTriggers();
    }, []);

    useEffect(() => {
        if (allTriggersList.length > 0 && !currentTriggerId) {
            selectTrigger(allTriggersList[0].id);
        }
    }, [allTriggersList]); // eslint-disable-line

    const savePositionsToBackend = async (nodeIds, network) => {
        const positionsToSave = nodeIds
            .filter(id => !String(id).startsWith('trigger_'))
            .map(id => {
                const pos = network.getPositions([id])[id];
                return { id: id, x: Math.round(pos.x), y: Math.round(pos.y) };
            });
        if (positionsToSave.length === 0) return;
        try {
            await axios.post(`${BASE_URL}/api/flow/save-node-positions/`, { positions: positionsToSave });
        } catch (err) {
            console.error("Error saving positions:", err);
        }
    };

    const selectTrigger = async (triggerId) => {
        setCurrentTriggerId(triggerId);
        hideEditor();
        await loadFlow(triggerId);
    };

    const loadFlow = async (triggerId) => {
        try {
            const res = await axios.get(`${BASE_URL}/api/flow-data/?trigger_id=${triggerId}`);
            const data = res.data;
            setAllNodesList(data.all_nodes || []);
            setAllProductsList(data.products || []);
            setCurrentTrigger(data.trigger);
            
            const hasCustomPositions = data.nodes.some(n => n.x !== undefined && n.y !== undefined && !String(n.id).startsWith('trigger_'));
            const useHierarchical = userSelectedLayoutMode !== null ? userSelectedLayoutMode : !hasCustomPositions;
            setIsHierarchical(useHierarchical);
            
            if (networkRef.current) {
                networkRef.current.setOptions({ layout: { hierarchical: { enabled: useHierarchical } } });
            }

            nodesDataset.clear();
            edgesDataset.clear();
            nodesDataset.add(data.nodes);
            edgesDataset.add(data.edges);
            
            const loadedIds = new Set(nodesDataset.getIds().map(String));
            const unlinked = (data.all_nodes || []).filter(n => !loadedIds.has(n.id));
            setUnlinkedNodes(unlinked);
            
            setTimeout(() => networkRef.current && networkRef.current.fit({ animation: true }), 50);
        } catch (error) {
            toast.error("Failed to load this flow");
        }
    };

    const refreshTriggersAndReconcile = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/flow/triggers/`);
            const triggers = res.data.triggers || [];
            setAllTriggersList(triggers);
            if (triggers.some(t => t.id === currentTriggerId)) {
                await loadFlow(currentTriggerId);
            } else if (triggers.length > 0) {
                await selectTrigger(triggers[0].id);
            } else {
                setCurrentTriggerId(null);
                setCurrentTrigger(null);
                nodesDataset.clear();
                edgesDataset.clear();
                setUnlinkedNodes([]);
                await fetchLookupData();
            }
        } catch(e) {}
    };

    // Editor Functions
    const loadNodeToEditor = useCallback((nodeId) => {
        const node = nodesDataset.get(nodeId);
        if (!node) return;
        setSelectedNodeId(nodeId);
        setNodeForm({
            id: nodeId,
            name: node.name || '',
            node_type: node.node_type || 'button',
            header_text: node.header_text || '',
            footer_text: node.footer_text || '',
            body_text: node.body_text || '',
            product_id: node.product_id || '',
            product_lang: node.product_lang || 'hi',
            options: JSON.parse(JSON.stringify(node.options || []))
        });
        
        const incoming = edgesDataset.get().filter(e => String(e.to) === String(nodeId));
        setEntryPoints(incoming);
    }, [nodesDataset, edgesDataset]);

    const hideEditor = () => {
        setSelectedNodeId(null);
        setNodeForm(null);
    };

    const createNewNode = () => {
        if (networkRef.current) networkRef.current.unselectAll();
        setSelectedNodeId('new');
        setNodeForm({
            id: '',
            name: 'New Step Node',
            node_type: 'button',
            header_text: '',
            footer_text: '',
            body_text: 'Hello! Please select an option below:',
            product_id: '',
            product_lang: 'hi',
            options: []
        });
        setEntryPoints([]);
    };

    const saveNode = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${BASE_URL}/api/flow/save-node/`, nodeForm);
            if (res.data.status === 'success') {
                await refreshTriggersAndReconcile();
                hideEditor();
                toast.success('Node saved.');
            } else {
                toast.error(res.data.message || 'Error saving flow node');
            }
        } catch (error) {
            toast.error("Error saving flow node");
        }
    };

    const deleteCurrentNode = async () => {
        if (!nodeForm.id) return hideEditor();
        if (!window.confirm("Are you sure you want to delete this node?")) return;
        try {
            const res = await axios.post(`${BASE_URL}/api/flow/delete-node/`, { id: nodeForm.id });
            if (res.data.status === 'success') {
                await refreshTriggersAndReconcile();
                hideEditor();
                toast.success('Node deleted.');
            }
        } catch (error) {
            toast.error("Error deleting node");
        }
    };

    const addOptionRow = () => {
        if (nodeForm.node_type === 'button' && nodeForm.options.length >= 3) {
            return toast.error("Buttons support max 3 choices.");
        }
        if (nodeForm.node_type === 'list' && nodeForm.options.length >= 10) {
            return toast.error("Lists support max 10 choices.");
        }
        setNodeForm(prev => ({
            ...prev,
            options: [...prev.options, { id: 'temp_' + Date.now(), button_title: `Option ${prev.options.length + 1}`, target_node_id: '', description: '' }]
        }));
    };

    const removeOptionRow = (index) => {
        setNodeForm(prev => {
            const newOpts = [...prev.options];
            newOpts.splice(index, 1);
            return { ...prev, options: newOpts };
        });
    };

    const updateOption = (index, field, value) => {
        setNodeForm(prev => {
            const newOpts = [...prev.options];
            newOpts[index][field] = value;
            return { ...prev, options: newOpts };
        });
    };

    // Trigger Modal
    const openTriggerModal = (prefill = {}) => {
        setTriggerModal({
            open: true,
            data: {
                id: prefill.id || '',
                exact_text: prefill.exact_text || '',
                target_node_id: prefill.target_node_id || ''
            }
        });
    };

    const editTriggerById = (id) => {
        const t = allTriggersList.find(x => x.id === id);
        if (t) openTriggerModal(t);
    };

    const saveTriggerFromModal = async () => {
        if (!triggerModal.data.exact_text) return toast.error("Trigger text is required.");
        try {
            const res = await axios.post(`${BASE_URL}/api/flow/save-trigger/`, triggerModal.data);
            if (res.data.status === 'success') {
                setTriggerModal({ open: false, data: {} });
                toast.success('Trigger saved.');
                await fetchTriggers();
                await selectTrigger(res.data.id);
            } else {
                toast.error(res.data.message);
            }
        } catch (e) {
            toast.error("Failed to save trigger");
        }
    };

    const deleteTriggerFromModal = async () => {
        if (!window.confirm("Delete this trigger? The target node itself will NOT be deleted.")) return;
        try {
            const res = await axios.post(`${BASE_URL}/api/flow/delete-trigger/`, { id: triggerModal.data.id });
            if (res.data.status === 'success') {
                setTriggerModal({ open: false, data: {} });
                toast.success('Trigger deleted.');
                await refreshTriggersAndReconcile();
            }
        } catch (e) {
            toast.error("Failed to delete trigger");
        }
    };

    const filteredTriggers = allTriggersList.filter(t => t.exact_text.toLowerCase().includes(triggerSearch.toLowerCase()));

    return (
        <div className="flow-builder-root">
            <header className="app-header">
                <div className="flex items-center gap-3">
                    <h5 className="mb-0 text-white flex items-center text-lg font-bold">
                        <FaWhatsapp className="text-green-500 mr-2" /> Flow Engine Visualizer
                    </h5>
                    <div className="ml-4">
                        <span className="legend-badge bg-trigger text-xs py-1 px-2 rounded-full mr-2">⚡ Trigger</span>
                        <span className="legend-badge bg-button text-xs py-1 px-2 rounded-full mr-2">🔘 Button</span>
                        <span className="legend-badge bg-list text-xs py-1 px-2 rounded-full mr-2">📋 List</span>
                        <span className="legend-badge bg-text text-xs py-1 px-2 rounded-full mr-2">💬 Text</span>
                        <span className="legend-badge bg-product text-xs py-1 px-2 rounded-full">🛍️ Product</span>
                    </div>
                    {currentTrigger && (
                        <span id="current-flow-label" className="text-gray-400 text-sm border-l border-gray-600 pl-3 ml-3">
                            ⚡ "{currentTrigger.exact_text}"
                        </span>
                    )}
                </div>
                <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-3 rounded flex items-center text-sm" onClick={createNewNode}>
                    <FaPlus className="mr-1" /> New Flow Node
                </button>
            </header>

            {showHintBar && (
                <div id="hint-bar">
                    <span className="flex items-center">
                        <FaLightbulb className="mr-2 text-yellow-300" /> A node's <strong>Connected Paths</strong> are the buttons users tap from it. To make a node reachable: add it as a path from another node, or turn it into a <strong>Trigger</strong> from the left sidebar.
                    </span>
                    <button onClick={() => setShowHintBar(false)} title="Dismiss"><MdClose /></button>
                </div>
            )}

            <div id="app-container">
                <aside id="trigger-sidebar">
                    <div className="sidebar-header">
                        <h6 className="mb-0 text-white flex items-center text-sm font-semibold">
                            <FaBolt className="text-yellow-500 mr-2" /> Flows
                        </h6>
                        <div className="flex items-center gap-2">
                            <span className="bg-gray-600 text-white text-xs px-2 py-0.5 rounded-full">{allTriggersList.length}</span>
                            <button className="text-gray-300 hover:text-white" onClick={() => openTriggerModal()} title="New Trigger">
                                <FaPlus />
                            </button>
                        </div>
                    </div>
                    <div className="sidebar-search">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs" />
                            <input type="text" className="w-full bg-gray-800 text-white text-sm border border-gray-600 rounded pl-8 pr-3 py-1.5 focus:outline-none focus:border-blue-500" placeholder="Filter flows..." value={triggerSearch} onChange={e => setTriggerSearch(e.target.value)} />
                        </div>
                    </div>
                    <div id="trigger-list">
                        {filteredTriggers.length === 0 ? (
                            <div className="trigger-empty">No triggers found.</div>
                        ) : (
                            filteredTriggers.map(t => (
                                <div key={t.id} className={`trigger-item ${t.id === currentTriggerId ? 'active' : ''} ${!t.has_target ? 'no-target' : ''}`} onClick={() => selectTrigger(t.id)}>
                                    <div className="trigger-item-top">
                                        <div className="trigger-text">⚡ "{t.exact_text}"</div>
                                        <button className="trigger-action-btn" title="Edit trigger" onClick={(e) => { e.stopPropagation(); editTriggerById(t.id); }}>
                                            <FaPen />
                                        </button>
                                    </div>
                                    <div className={`trigger-target ${!t.has_target ? 'warn' : ''}`}>
                                        {t.has_target ? '→ ' + t.target_node_name : '⚠ No target node linked — click ✎ to link one'}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </aside>

                <div id="network-canvas-wrap">
                    <div className="canvas-toolbar">
                        <button className="text-gray-400 hover:text-white px-2" onClick={() => networkRef.current?.moveTo({ scale: networkRef.current.getScale() * 1.5, animation: true })} title="Zoom In"><FaSearchPlus /></button>
                        <button className="text-gray-400 hover:text-white px-2" onClick={() => networkRef.current?.moveTo({ scale: networkRef.current.getScale() / 1.5, animation: true })} title="Zoom Out"><FaSearchMinus /></button>
                        <button className="text-gray-400 hover:text-white px-2" onClick={() => networkRef.current?.fit({ animation: true })} title="Fit View"><FaExpand /></button>
                        <div className="w-px h-4 bg-gray-600 mx-2"></div>
                        <button className="text-gray-400 hover:text-white px-2 flex items-center text-xs" onClick={() => {
                            const next = !isHierarchical;
                            setIsHierarchical(next);
                            setUserSelectedLayoutMode(next);
                            networkRef.current?.setOptions({ layout: { hierarchical: { enabled: next } } });
                        }}>
                            {isHierarchical ? <FaProjectDiagram className="mr-1"/> : <FaArrowsAlt className="mr-1"/>}
                            {isHierarchical ? 'Hierarchical' : 'Manual Freeform'}
                        </button>
                        
                        {unlinkedNodes.length > 0 && (
                            <button className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-2 py-1 rounded ml-4 flex items-center" onClick={() => setShowUnlinkedPanel(!showUnlinkedPanel)}>
                                <FaExclamationTriangle className="mr-1" /> {unlinkedNodes.length} unlinked
                            </button>
                        )}
                    </div>

                    {showUnlinkedPanel && (
                        <div id="unlinked-panel" style={{display: 'block'}}>
                            <div className="text-xs font-bold text-yellow-500 mb-1 flex items-center"><FaUnlink className="mr-1"/>Not part of this flow</div>
                            <div className="unlinked-hint">These nodes exist but aren't reachable from the flow currently on screen. Tap ⚡ to turn one into a new Trigger root, or open another node and add it as a path.</div>
                            <div>
                                {unlinkedNodes.map(n => (
                                    <div key={n.id} className="unlinked-item flex justify-content-between items-center gap-2">
                                        <span>{n.name}</span>
                                        <button className="bg-transparent border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white p-1 rounded" title="Make this a new Trigger root" onClick={() => openTriggerModal({target_node_id: n.id})}>
                                            <FaBolt size={10} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div id="network-canvas" ref={containerRef}></div>
                    
                    {!currentTriggerId && (
                        <div id="canvas-empty-state">
                            <FaProjectDiagram size={40} className="mb-4" />
                            <p className="mb-4">Pick a flow from the left panel to view its conversation tree.</p>
                            <button className="border border-gray-400 text-gray-300 hover:text-white hover:border-white px-4 py-2 rounded flex items-center text-sm" onClick={() => openTriggerModal()}>
                                <FaPlus className="mr-2" /> Create your first Trigger
                            </button>
                        </div>
                    )}
                </div>

                <aside id="editor-panel">
                    <div className="panel-header flex justify-between items-center">
                        <h6 className="mb-0 text-white text-sm font-semibold">{nodeForm ? "Editing Node" : "Node Inspector"}</h6>
                        <span className="bg-gray-600 text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase">
                            {nodeForm ? (nodeForm.id ? nodeForm.node_type : 'NEW') : 'NONE'}
                        </span>
                    </div>
                    <div className="panel-body">
                        {!nodeForm ? (
                            <div className="text-center py-10 text-gray-500 flex flex-col items-center">
                                <FaHandPointer size={30} className="mb-4" />
                                <p className="text-sm">Click any Node on the graph to inspect, edit paths, or view.</p>
                            </div>
                        ) : (
                            <form onSubmit={saveNode}>
                                <label className="text-gray-400 text-[11px] font-bold mb-2 flex items-center"><FaWhatsapp className="text-green-500 mr-1" /> LIVE WHATSAPP PREVIEW</label>
                                <div className="wa-preview-card">
                                    <div className="wa-bubble">
                                        {nodeForm.header_text && <div className="wa-header">{nodeForm.header_text}</div>}
                                        <div style={{ whiteSpace: "pre-wrap" }}>{nodeForm.body_text || 'Message text...'}</div>
                                        <div className="wa-footer">{nodeForm.footer_text || '12:00 PM'}</div>
                                    </div>
                                    <div className="mt-2">
                                        {nodeForm.node_type === 'button' && nodeForm.options.map((opt, i) => (
                                            <div key={i} className="wa-button-option flex items-center justify-center"><FaReply className="mr-1" /> {opt.button_title || 'Button'}</div>
                                        ))}
                                        {nodeForm.node_type === 'list' && (
                                            <div className="wa-button-option flex items-center justify-center"><FaBars className="mr-1" /> Select Choice ({nodeForm.options.length})</div>
                                        )}
                                    </div>
                                </div>

                                <div id="entry-points-box">
                                    <div className="entry-points-title"><FaProjectDiagram /> Entry Points</div>
                                    <div>
                                        {entryPoints.length === 0 ? (
                                            <>
                                                <div className="entry-empty-new mb-2">
                                                    No entry point in the loaded flow.
                                                </div>
                                                <button type="button" className="border border-yellow-600 text-yellow-500 hover:bg-yellow-600 hover:text-white px-2 py-1 rounded text-xs flex items-center" onClick={() => openTriggerModal({target_node_id: nodeForm.id})}>
                                                    <FaBolt className="mr-1" /> Make this a new Trigger root
                                                </button>
                                            </>
                                        ) : (
                                            entryPoints.map((e, i) => {
                                                if (String(e.from).startsWith('trigger_')) {
                                                    const trig = allTriggersList.find(t => t.id === String(e.from).replace('trigger_', ''));
                                                    return <div key={i} className="entry-item">⚡ Trigger: "{trig?.exact_text}"</div>;
                                                }
                                                const sourceNode = nodesDataset.get(e.from);
                                                return <div key={i} className="entry-item">↩ From "{sourceNode?.name}" via "{e.label?.trim()}"</div>;
                                            })
                                        )}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="text-gray-300 text-xs mb-1 block">Internal Identifier Name</label>
                                    <input type="text" className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none" required value={nodeForm.name} onChange={e => setNodeForm({...nodeForm, name: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label className="text-gray-300 text-xs mb-1 block">Node Action Type</label>
                                    <select className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none" value={nodeForm.node_type} onChange={e => setNodeForm({...nodeForm, node_type: e.target.value})}>
                                        <option value="button">Button (Max 3 options)</option>
                                        <option value="list">List Menu (Max 10 options)</option>
                                        <option value="text">Plain Text (Terminal)</option>
                                        <option value="product">Product Dispatcher</option>
                                    </select>
                                </div>
                                
                                <div className="flex gap-2 mb-3">
                                    <div className="flex-1">
                                        <label className="text-gray-300 text-xs mb-1 block">Header (Opt)</label>
                                        <input type="text" className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none" value={nodeForm.header_text} onChange={e => setNodeForm({...nodeForm, header_text: e.target.value})} />
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-gray-300 text-xs mb-1 block">Footer (Opt)</label>
                                        <input type="text" className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none" value={nodeForm.footer_text} onChange={e => setNodeForm({...nodeForm, footer_text: e.target.value})} />
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="text-gray-300 text-xs mb-1 block">Message Body Text</label>
                                    <textarea className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none" rows="3" required value={nodeForm.body_text} onChange={e => setNodeForm({...nodeForm, body_text: e.target.value})}></textarea>
                                </div>

                                {nodeForm.node_type === 'product' && (
                                    <div className="mb-3 bg-gray-800 p-2 rounded border border-gray-600">
                                        <label className="text-yellow-500 text-xs mb-1 flex items-center"><FaBox className="mr-1" /> Link Catalog Product</label>
                                        <select className="w-full bg-gray-700 text-white border-0 rounded px-2 py-1 text-sm mb-2 focus:outline-none" value={nodeForm.product_id} onChange={e => setNodeForm({...nodeForm, product_id: e.target.value})}>
                                            <option value="">-- Choose Product --</option>
                                            {allProductsList.map(p => <option key={p.id} value={p.id}>{p.name_en} ({p.name_hi})</option>)}
                                        </select>
                                        <label className="text-gray-400 text-[10px] mb-1 block">Language Format</label>
                                        <select className="w-full bg-gray-700 text-white border-0 rounded px-2 py-1 text-sm focus:outline-none" value={nodeForm.product_lang} onChange={e => setNodeForm({...nodeForm, product_lang: e.target.value})}>
                                            <option value="hi">Hindi (hi)</option>
                                            <option value="en">English (en)</option>
                                        </select>
                                    </div>
                                )}

                                {(nodeForm.node_type === 'button' || nodeForm.node_type === 'list') && (
                                    <div className="mb-3">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-blue-400 text-[11px] font-bold flex items-center"><FaRoute className="mr-1" /> CONNECTED PATHS</label>
                                            <button type="button" className="border border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-2 py-0.5 rounded text-xs flex items-center" onClick={addOptionRow}><FaPlus className="mr-1" /> Add Path</button>
                                        </div>
                                        <span className="field-hint mb-2">Each path becomes a button/list item users tap from this node, leading to the target node you choose.</span>
                                        <div>
                                            {nodeForm.options.length === 0 ? (
                                                <small className="text-gray-500 italic">No paths attached.</small>
                                            ) : (
                                                nodeForm.options.map((opt, i) => (
                                                    <div key={i} className="option-item-card">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="text-xs font-bold text-gray-400">Choice #{i + 1}</span>
                                                            <button type="button" className="text-red-400 hover:text-red-300" onClick={() => removeOptionRow(i)}><MdClose size={16} /></button>
                                                        </div>
                                                        <input type="text" className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-xs mb-1 focus:outline-none" placeholder="Button Label (Max 20 chars)" maxLength="20" required value={opt.button_title} onChange={e => updateOption(i, 'button_title', e.target.value)} />
                                                        <select className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-xs focus:outline-none" value={opt.target_node_id} onChange={e => updateOption(i, 'target_node_id', e.target.value)}>
                                                            <option value="">-- Target Node --</option>
                                                            {allNodesList.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                                                        </select>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-2 pt-3 border-t border-gray-600 mt-4">
                                    <button type="submit" className="bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-1.5 px-3 rounded flex-1">Save Node</button>
                                    <button type="button" className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white py-1.5 px-3 rounded flex items-center justify-center" onClick={deleteCurrentNode}><FaTrash /></button>
                                </div>
                            </form>
                        )}
                    </div>
                </aside>
            </div>

            {/* Trigger Modal */}
            {triggerModal.open && (
                <div className="modal-backdrop-custom" onClick={(e) => { if (e.target === e.currentTarget) setTriggerModal({open: false, data: {}})}}>
                    <div className="modal-box-custom">
                        <div className="modal-box-header">
                            <h6>{triggerModal.data.id ? 'Edit Trigger' : 'New Trigger'}</h6>
                            <button type="button" onClick={() => setTriggerModal({open: false, data: {}})}><MdClose /></button>
                        </div>
                        <div className="modal-box-body">
                            <div className="mb-3">
                                <label className="text-gray-300 text-xs mb-1 block">Trigger Text</label>
                                <input type="text" className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none" placeholder="e.g. Buy Zatka Machine" value={triggerModal.data.exact_text} onChange={e => setTriggerModal(prev => ({...prev, data: {...prev.data, exact_text: e.target.value}}))} />
                                <span className="field-hint">The exact CTA / button text WhatsApp sends when a user taps it. Matched exactly, so avoid extra spaces or punctuation.</span>
                            </div>
                            <div>
                                <label className="text-gray-300 text-xs mb-1 block">Target Node (flow root)</label>
                                <select className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none" value={triggerModal.data.target_node_id} onChange={e => setTriggerModal(prev => ({...prev, data: {...prev.data, target_node_id: e.target.value}}))}>
                                    <option value="">-- No target yet (unlinked) --</option>
                                    {allNodesList.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                                </select>
                                <span className="field-hint">The first node users land on when this trigger fires. You can leave this blank and link it later.</span>
                            </div>
                        </div>
                        <div className="modal-box-footer">
                            {triggerModal.data.id && (
                                <button type="button" className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-3 py-1 rounded text-sm flex items-center" onClick={deleteTriggerFromModal}><FaTrash className="mr-1" /> Delete</button>
                            )}
                            <div className="flex-1"></div>
                            <button type="button" className="text-gray-400 hover:text-white px-3 py-1 text-sm" onClick={() => setTriggerModal({open: false, data: {}})}>Cancel</button>
                            <button type="button" className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-bold" onClick={saveTriggerFromModal}>Save Trigger</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlowBuilder;
